import { Component, OnInit, ViewChild, ElementRef, Input, ViewEncapsulation, Output, EventEmitter, HostListener, SimpleChanges, OnDestroy, OnChanges, effect } from '@angular/core';
import {cloneDeep, debounce} from 'lodash'
import * as d3 from 'd3';
import { ShotType } from '../../../../models/shotType';

@Component({
    selector: 'app-shots-graph',
    encapsulation: ViewEncapsulation.None,
    templateUrl: './shots-graph.component.html',
    styleUrls: ['./shots-graph.component.css'],
    standalone: false
})
export class ShotsGraphComponent implements OnInit, OnDestroy, OnChanges {
  @Input() hideAxes: boolean = false
  @Input() colorMap: string[] = ['black', 'grey', 'white']
  xScale: any;
  yScale: any;
  @Input() set radius(value:number){
    this._radius = value;
    this.drawAfter(50);
  }
  @Input() set shotData(value: {x: number, y:number, shotId: number, hasTraceFile: boolean, traces_fit: number}[]) {
    this._shotData = value;
 
    this.drawAfter(50);
  }

  @Input() set rcvrData(value: {x: number, y:number, shotId: number, hasTraceFile: boolean, traces_fit: number}[]) {
    this._rcvrData = value;

    this.drawAfter(50);
  }

  @Input() shotType: ShotType;

  @Input() set plotRcvrs(value: boolean) {
    this._plotRcvrs = value;

    this.drawAfter(50);
  }

  @Input() set selectRcvrs(value: boolean) {
    this._selectRcvrs = value
    this.drawAfter(50)
  }

  @Input() wellLogs: {x: number, y: number}[]

  @Input() set backgroundImage (value: string) {
    this._backgroundImage = value;

    this.drawAfter(50);
  }
  @Input() set height(value: number) {
    this._height =value;
  
    this.drawAfter(50);
  }
  //@Input() modelScale: {x: number, y: number}
  @Input() xCrop?: () => number[]
  @Input() yCrop?: () => number[]
  @Input() set selectedShot(value: number) {
      this._selectedShot = value;
      this.drawAfter(50);  
  }
  @Input() set colourType(value: string) {
    this._colourType = value;
  
    this.drawAfter(50);
  }
  @Input() set colorScale(value: {min: number, max: number}) {
    this.minTraceFit = value.min;
    this.maxTraceFit = value.max;

    this.drawAfter(50);
  }

  @Input() showNA: boolean = true;

  @Input() isMainImage: boolean = false;

  @ViewChild('graph', { static: true }) graph: ElementRef;
  @ViewChild('graphContainer', { static: true }) container: ElementRef;
  @Output('onShotSelected') shotSelection: EventEmitter<number> = new EventEmitter<number>();
  @Output('traceFit') traceFit: EventEmitter<number> = new EventEmitter<number>();
  @Output('onRcvrsSelected') onRcvrsSelected: EventEmitter<{rcvrs: object[], limits: any[]}> = new EventEmitter<{rcvrs: object[], limits: []}>();

  @Input('rcvrsBoundingBox') rcvrsBoundingBox!: any[];
  @Output() rcvrsBoundingBoxChange = new EventEmitter<any[]>();

  private width: number;
  private _height: number = 200;
  private _shotData:  {x: number, y:number, shotId: number, hasTraceFile: boolean, traces_fit: number, traces_raw?: number}[];
  private _rcvrData:  {x: number, y:number, shotId: number, hasTraceFile: boolean, traces_fit: number, traces_raw?: number}[];
  private _backgroundImage: string
  private _selectedShot: number;
  private _colourType: string;
  private svg;
  private updateDelay;
  private minTraceFit = 0;
  private maxTraceFit = 100;
  private _plotRcvrs = false;
  private _selectRcvrs = false;
  private brush: any
  activeSelectedBox: any[] = null
  padding = {left:30, bottom:30, top: 0}
  _radius=3;
  private static clipIdCounter = 0;
  private readonly clipId = `shots-graph-clip-${ShotsGraphComponent.clipIdCounter++}`;

  constructor() {
    effect(() => {
      this.xCrop?.();
      this.yCrop?.();
      this.drawAfter(50);
    });
  }

  ngOnInit() {
  }

  ngOnDestroy() {
  }

  @HostListener('window:resize', ['$event'])
  onresize(event) {
    this.resizeWithDelay(event);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.wellLogs){
      this.updateGraph();
    }
    if (changes.showNA){
      this.updateGraph();
    }
    if ('rcvrsBoundingBox' in changes){
      if (this.brush && this._selectRcvrs){
        this.makeBrushBox()
      }
    }
  }

  private resizeWithDelay = debounce((event) => {
    this.updateGraph();
  }, 100);

  updateGraph() {
    // Remove previous shot graph
    if(this.svg) {
      this.svg.remove();
    }

    if (!this._shotData) return;

    let filteredShotData = this._shotData;
    if (!this.showNA) {
      if (this._colourType === 'traces_fit') {
        filteredShotData = filteredShotData.filter(d => d.traces_fit != null);
      } else if (this._colourType === 'traces_raw') {
        filteredShotData = filteredShotData.filter(d => d.traces_raw != null);
      }
    }

    // if(this._selectedShot &&
    //    !(this._shotData.filter(s => s.hasTraceFile).map(s => s != null? s.shotId : null).includes(this._selectedShot))) {
    //   this._selectedShot = null;
    // }
    this.width = this.container.nativeElement.offsetWidth;
    // calculate the sizes of svg and scales for shot points
    this.draw(filteredShotData, this._rcvrData);
  }

  // just a function to make a star
  createStar(cx: number, cy: number, spikes: number, outerRadius: number, innerRadius: number): string {
    const points = [];
    const step = Math.PI / spikes; // Half step to alternate outer and inner points
    
    for (let i = 0; i < 2 * spikes; i++) {
      const radius = i % 2 === 0 ? outerRadius : innerRadius;
      const angle = i * step;
      points.push([
        cx + Math.cos(angle) * radius,
        cy - Math.sin(angle) * radius
      ]);
    }
  
    return d3.line()([...points, points[0]]); // Close the star path
  }

  makeBrushBox(){
    let [[x0, y0],[x1, y1]] = cloneDeep(this.rcvrsBoundingBox)
    if (
      typeof x0 !== 'number' || typeof y0 !== 'number' ||
      typeof x1 !== 'number' || typeof y1 !== 'number' ||
      x0 == null || y0 == null || x1 == null || y1 == null
    ) {
      return;
    }
    let newExtent = [[this.xScale(x0), this.yScale(y0)], [this.xScale(x1), this.yScale(y1)]]
    setTimeout(() => {
      this.svg.call(this.brush.move, newExtent)
    }, 50);
  }

  resizeBrush(extent: any){
    if (this.brush && extent){
      const [[x0, y0], [x1, y1]] = extent; 
      let newExtent = [[this.xScale(x0), this.yScale(y0)], [this.xScale(x1), this.yScale(y1)]]
      this.svg.call(this.brush?.move, newExtent);
    }
  }

  // Resolve a crop range to [min, max], filling in either end from the plotted
  // points when the crop input is absent or still holds a null bound.
  private cropOrExtent(crop: () => number[], data, rcvrData, axis: 'x' | 'y'): [number, number] {
    const [cropMin, cropMax] = typeof crop === 'function' ? (crop() ?? []) : [];
    if (cropMin != null && cropMax != null) {
      return [cropMin, cropMax];
    }

    const points = [...(data ?? []), ...(this._plotRcvrs ? rcvrData ?? [] : [])];
    const [extentMin, extentMax] = d3.extent(points, (d: any) => d[axis]);
    return [
      cropMin ?? extentMin ?? 0,
      cropMax ?? extentMax ?? 0,
    ];
  }

  private draw(data, rcvrData?) {
    // xCrop/yCrop are optional: callers that don't own a crop range (or whose
    // range hasn't been resolved from the job yet) fall back to the data extent.
    var [x1, x2] = this.cropOrExtent(this.xCrop, data, rcvrData, 'x');
    var [y1, y2] = this.cropOrExtent(this.yCrop, data, rcvrData, 'y');
    this.xScale = d3.scaleLinear()
                   .domain([x1, x2])
                   .range([this.padding.left, this.width]);

    let domain = [y1, y2];
    if (domain[1] < 0) {
      domain = [-domain[1], domain[1]*0.03]
    }
    this.yScale = d3.scaleLinear()
                   .domain(domain)
                   .range([this._height - this.padding.bottom, this.padding.top]);
    let colourScale = d3.scaleLinear()
                        .domain([this.minTraceFit, (this.maxTraceFit+this.minTraceFit)/2, this.maxTraceFit])
                        .range(this.colorMap as any);
    
    let kilometerFormatter = d3.format(".1f");
    
    let xAxis = d3.axisBottom(this.xScale)
      .ticks(this.hideAxes ? 0 : 5)
      .tickSize(5)
      .tickPadding(5)
      .tickFormat(function(d: any) {
        return kilometerFormatter(d / 1000); // Convert meters to kilometers
      });

    let yAxis = d3.axisLeft(this.yScale)
      .ticks(this.hideAxes ? 0 : 3)
      .tickSize(5)
      .tickPadding(5)
      .tickFormat(function(d: any) {
        return kilometerFormatter(d / 1000); // Convert meters to kilometers
      });

    let step = 255 / (this.maxTraceFit - this.minTraceFit)
    let color = (value) => {
      let c = Math.round( (value - this.minTraceFit) * step);
      if (c < 0) {
        c = 0;
      }
      if (c > 255) {
        c = 255;
      }
      return c;
    }

    let svg = d3.select(this.graph.nativeElement)
                .attr("width", this.width)
                .attr("height", this._height)
                .append("g").attr("id", "svg");
    this.svg = svg;

    // Background image
    let bgImage = svg.append("g").attr("id", "bgImage")
       .append("image")
       .attr("transform", `translate(${this.padding.left},${this.padding.top})`)
       .attr("width", this.width - this.padding.left).attr("height", this._height - this.padding.bottom - this.padding.top)
       .attr('preserveAspectRatio', 'none')
       .attr("xlink:href", this._backgroundImage)
       .attr('fill', 'white')
       .attr('opacity',0.3);

    let colorType = this._colourType;
    let distinguishColor = this.colorMap.includes('red') ? 'black' : 'red'
    // Shot points

    // Clip shots/receivers to the background image's bounds so points outside
    // the current xCrop/yCrop range don't render past it into the padding.
    // The id must be unique per instance: several graphs of different sizes can
    // share a page, and url(#id) always resolves to the first match in the
    // document, so a shared id lets one graph clip another to its own bounds.
    svg.append("clipPath")
       .attr("id", this.clipId)
       .append("rect")
       .attr("x", this.padding.left)
       .attr("y", this.padding.top)
       .attr("width", bgImage.attr("width"))
       .attr("height", bgImage.attr("height"));

    let radius = this._radius;
    let shots = svg.append("g").attr("id", "circles")
      .attr("clip-path", `url(#${this.clipId})`);
    let isMainImage = this.isMainImage;

    let individualShots = shots.selectAll("circle").data(data).enter().append("circle")
         .attr("cx", (d: any) => {return this.xScale(d.x)})
         .attr("cy", (d: any) => {return this.yScale(d.y)})
         .attr("r",  (d: any) => {
          let r = radius != null ? radius : 3;
          return d.hasTraceFile? r+2: r;})
         .attr("fill", (d: any) => {
          if (isMainImage) {
            return colourScale(d.traces_fit);
          } else {
            let grayscale = color(d[colorType])
            let resultColor = `rgb(${grayscale},${grayscale},${grayscale})`;
            return resultColor;
          }
         })
         .attr("stroke", function(d) { 
          if (d.shotId === this._selectedShot) {
            return '#343434'; // Black outline for selected shot
          }
          return d.hasTraceFile ? distinguishColor : 'none';
        }.bind(this))
        .attr("stroke-width", function(d) {
          if (d.shotId === this._selectedShot) {
            return 4; // Thicker stroke for selected shot
          }
          return 2;
        }.bind(this))
        .classed("selected", (d: any) => {return d.shotId == this._selectedShot})
        .classed("selectable", (d: any) => d.hasTraceFile)
         .on("mouseover", function(event, d) {
            d3.select(this)
              .attr("r", function(d: any) {
                let r = radius != null ? radius : 3;
                return d.hasTraceFile? r+5 : r;
              }).style("cursor", "pointer");
          })
         .on("mouseout", function(event, d) {
            d3.select(this)
              .attr("r", function(d: any) {
                let r = radius != null ? radius : 3;
                return d.hasTraceFile? r+2 : r;
              });
          })
         .on("click", (event, d: any) => {
          if (this._selectedShot === d.shotId) {
            return;
          }
            this._selectedShot = d.shotId;
            let id = d.shotId

            shots.selectAll("circle").classed("selected", (d: any)=>d.shotId == id);

            shots.selectAll("circle")
            .attr("stroke", function(circleData: any) {
              if (circleData.shotId === id) {
                return '#343434'; // Black outline for selected shot
              }
              return circleData.hasTraceFile ? distinguishColor : 'none';
            })
            .attr("stroke-width", function(circleData: any) {
              if (circleData.shotId === id) {
                return 4; // Thicker stroke for selected shot
              }
              return 2;
            });

            this.shotSelection.emit(d.shotId);
         });

    if (rcvrData && this._plotRcvrs) {
      const rcvrs = shots.append("g").attr("id", "rcvrCircles")
         .selectAll("circle").data(rcvrData).enter().append("circle")
         .attr("cx", (d: any) => {return this.xScale(d.x)})
         .attr("cy", (d: any) => {return this.yScale(d.y)})
         .attr("r",  (d) => {return 1.5})
         .attr("fill", (d: any) => {
           if (d.hasTraceFile && this.shotType.name != 'SelectRcvrs') {
             return 'orange';
           }
           return 'purple'
         }).each(function(d: any) {
          if (d.hasTraceFile) {
            this.parentNode.appendChild(this); // Move green circles to the end of the parent group
          }
        });

      // Raise selectable shots to be on top
      shots.selectAll(".selectable").raise();  

      if (this._selectRcvrs){
        // Limit brush to the plotting area within padding
        const extent = [
          [this.padding.left, this.padding.top],
          [this.width, this._height - this.padding.bottom]
        ];

        // Create the brush behavior.
        this.brush = d3.brush().extent(extent as any)
          .on("brush", ({selection, sourceEvent}) => {
          if (!sourceEvent) return;
          let selectedRcvrs = []
          if (selection) {
            const [[x0, y0], [x1, y1]] = cloneDeep(selection);

            selectedRcvrs = rcvrs
              .style("fill", 'purple')
              .filter((d: any) => x0 <= this.xScale(d.x) && this.xScale(d.x) < x1
                && y0 <= this.yScale(d.y) && this.yScale(d.y) < y1)
              .style("fill", "orange").data();

            let converted = [[this.xScale.invert(x0), this.yScale.invert(y0)], [this.xScale.invert(x1), this.yScale.invert(y1)]] // in real values
            this.rcvrsBoundingBoxChange.emit(converted)
          }
        })
        svg.call(this.brush);
        this.makeBrushBox()
      }
    }



    // show the well locations if available
    if (this.wellLogs){
      let wells = this.svg.append('g').attr('id', 'wells')
      for (let well of this.wellLogs){  
        wells
        .append("path")
        .attr('class', 'well-location')
        .attr('d', this.createStar(this.xScale(well.x), this.yScale(well.y), 5, 10, 5))
        .attr('fill', 'gold')
        .attr('stroke', 'black')
        .attr('stroke-width', 2);
      }
    }

    // Instantiate axis
    let gX = svg.append("g")
      .attr("transform", `translate(0, ${this._height - this.padding.bottom})`)
      .call(xAxis);
    let gY = svg.append("g")
      .attr("transform", `translate(${this.padding.left}, 0)`)
      .call(yAxis);

  }

  private drawAfter(time: number) {
    // Wait time amount of time before calling draw()
    // Prevent previous calling of draw() is this function is invoked
    // again in the amout of time.
    // Used to prevent multiple calls to draw() if inputs changes at the same time.
    clearTimeout(this.updateDelay);
    this.updateDelay = setTimeout(() => this.updateGraph(), time);
  }
}
